// A picture of the learner's tree to send to someone: the scene and tree as
// drawn in the sheet, on a card with its stage and harvest. Built from the
// live SVG rather than a second renderer, so the card is the tree they see.

import type { ShareOutcome } from "@aybuben/client/analytics/events.ts";
import { CARD_H, CARD_W, SCENE, SITE, type ShareText } from "@aybuben/client/tree/share-card.ts";
import { paletteFor } from "@aybuben/client/world/palette.ts";

// The card is an image that leaves the app: it keeps the light palette in either theme.
import { HEX as COL } from "../../theme.ts";

export { CARD_H, CARD_W, SITE, type ShareText };
const FONT = "system-ui, -apple-system, Segoe UI, Roboto, sans-serif";
const ARM = "Noto Sans Armenian, Noto Serif Armenian, Mshtakan, Sylfaen, sans-serif";

const esc = (s: string): string =>
	s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

/**
 * The card as one SVG document. `scene` and `tree` are serialized `<svg>`
 * elements; the scene fills a rounded square and the tree stands in it with
 * the scene's insets (2% above, 10% below).
 */
export function cardSvg(scene: string, tree: string, text: ShareText): string {
	const { x, y, size, radius } = SCENE;
	const place = (svg: string, top: number, h: number): string =>
		svg.replace(/^<svg\b/, `<svg x="${x}" y="${top}" width="${size}" height="${h}"`);
	return [
		`<svg xmlns="http://www.w3.org/2000/svg" width="${CARD_W}" height="${CARD_H}" viewBox="0 0 ${CARD_W} ${CARD_H}">`,
		`<rect width="${CARD_W}" height="${CARD_H}" fill="${COL.bg}"/>`,
		`<text x="${CARD_W / 2}" y="130" text-anchor="middle" font-family="${FONT}" font-size="64" font-weight="800" fill="${COL.ink}">${esc(text.title)}</text>`,
		`<clipPath id="card-scene"><rect x="${x}" y="${y}" width="${size}" height="${size}" rx="${radius}"/></clipPath>`,
		`<g clip-path="url(#card-scene)">${place(scene, y, size)}${place(tree, y + size * 0.02, size * 0.88)}</g>`,
		`<text x="${CARD_W / 2}" y="${y + size + 90}" text-anchor="middle" font-size="52" font-weight="800" fill="${COL.greenDark}"><tspan font-family="${FONT}">${esc(text.stage)} · </tspan><tspan font-family="${ARM}">${esc(text.hy)}</tspan></text>`,
		`<text x="${CARD_W / 2}" y="${y + size + 160}" text-anchor="middle" font-family="${FONT}" font-size="38" font-weight="600" fill="${COL.mute}">${esc(text.caption)}</text>`,
		`<text x="${CARD_W / 2}" y="${CARD_H - 40}" text-anchor="middle" font-family="${FONT}" font-size="30" font-weight="800" fill="${COL.pom}">${SITE}</text>`,
		`</svg>`,
	].join("");
}

/**
 * A still copy of a drawn `<svg>`: no classes or tap targets, nothing left
 * waiting on an animation — a branch still to be drawn in is hidden by its
 * dash offset, which no stylesheet will clear inside an image — and the
 * palette's `var(--c-<role>)` colours written out in the light palette, which
 * no stylesheet defines there either.
 */
export function stillSvg(svg: SVGSVGElement): string {
	const copy = svg.cloneNode(true);
	if (!(copy instanceof Element)) return "";
	for (const el of copy.querySelectorAll('[data-hit], [data-part="petal"]')) el.remove();
	for (const el of [copy, ...copy.querySelectorAll("*")]) {
		el.removeAttribute("class");
		el.removeAttribute("stroke-dasharray");
		el.removeAttribute("stroke-dashoffset");
	}
	// The card sizes each drawing; a shape's own width (the sky's rect) stays.
	copy.removeAttribute("width");
	copy.removeAttribute("height");
	copy.setAttribute("xmlns", "http://www.w3.org/2000/svg");
	return new XMLSerializer()
		.serializeToString(copy)
		.replace(/var\(--c-([A-Za-z]+)\)/g, (v, role: string) =>
			// A palette role, checked by `in` just before.
			role in COL ? COL[role as keyof typeof COL] : v,
		);
}

export type { ShareOutcome };

function toPng(svg: string): Promise<Blob | null> {
	return new Promise((resolve) => {
		const img = new Image();
		img.addEventListener("load", () => {
			const canvas = document.createElement("canvas");
			canvas.width = CARD_W;
			canvas.height = CARD_H;
			const ctx = canvas.getContext("2d");
			if (!ctx) return resolve(null);
			ctx.drawImage(img, 0, 0);
			canvas.toBlob((b) => resolve(b), "image/png");
		});
		img.addEventListener("error", () => resolve(null));
		img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
	});
}

/** Shares a card image through the system sheet where the browser can, and downloads it where it cannot. */
async function shareImage(svg: string, fileName: string, text: string): Promise<ShareOutcome> {
	const png = await toPng(svg);
	if (!png) return "failed";
	const file = new File([png], fileName, { type: "image/png" });
	try {
		if (navigator.canShare?.({ files: [file] })) {
			await navigator.share({ files: [file], text });
			return "shared";
		}
	} catch (e) {
		if (e instanceof DOMException && e.name === "AbortError") return "cancelled";
	}
	const url = URL.createObjectURL(file);
	const a = document.createElement("a");
	a.href = url;
	a.download = file.name;
	document.body.append(a);
	a.click();
	a.remove();
	setTimeout(() => URL.revokeObjectURL(url), 10_000);
	return "downloaded";
}

export async function shareTree(scene: HTMLElement, text: ShareText): Promise<ShareOutcome> {
	const [backdrop, tree] = [...scene.querySelectorAll("svg")];
	if (!backdrop || !tree) return "failed";
	return shareImage(
		cardSvg(stillSvg(backdrop), stillSvg(tree), text),
		"aybuben-tree.png",
		`${text.text} https://${SITE}`,
	);
}

/** The Polaroid on a plain world sky over the ground, with the app's line and link. */
export function polaroidCardSvg(
	photo: string,
	p: { hy: string; name: string; date: string | null; line: string },
): string {
	// The card leaves the app: a fixed summer day, whatever the light of the photo.
	const sky = paletteFor("summer", "day");
	const W = CARD_W;
	const pw = 760;
	const x = (W - pw) / 2;
	const y = 170;
	const img = pw - 88;
	return [
		`<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${CARD_H}" viewBox="0 0 ${W} ${CARD_H}">`,
		`<defs><linearGradient id="polaroid-sky" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="${sky.skyTop}"/><stop offset="1" stop-color="${sky.skyBottom}"/></linearGradient></defs>`,
		`<rect width="${W}" height="${CARD_H * 0.55}" fill="url(#polaroid-sky)"/>`,
		`<rect y="${CARD_H * 0.55}" width="${W}" height="${CARD_H * 0.15}" fill="${sky.ochre}"/>`,
		`<rect y="${CARD_H * 0.7}" width="${W}" height="${CARD_H * 0.3}" fill="${sky.earth}"/>`,
		`<g transform="rotate(-3 ${W / 2} ${y + pw / 2})">`,
		`<rect x="${x}" y="${y}" width="${pw}" height="${pw + 180}" fill="#FFFDF8"/>`,
		photo.replace(/^<svg\b/, `<svg x="${x + 44}" y="${y + 44}" width="${img}" height="${img}"`),
		`<text x="${x + 44}" y="${y + img + 130}" font-family="${ARM}" font-size="64" font-weight="700" fill="${COL.ink}">${esc(p.hy)}</text>`,
		p.date
			? `<text x="${x + pw - 44}" y="${y + img + 130}" text-anchor="end" font-family="${FONT}" font-size="36" font-weight="700" fill="${COL.mute}">${esc(p.date)}</text>`
			: "",
		`<text x="${x + 44}" y="${y + img + 180}" font-family="${FONT}" font-size="40" font-weight="700" fill="${COL.mute}">${esc(p.name)}</text>`,
		`</g>`,
		`<text x="${W / 2}" y="${CARD_H - 110}" text-anchor="middle" font-family="${FONT}" font-size="46" font-weight="800" fill="#fff">${esc(p.line)} <tspan font-family="${ARM}">Այբուբեն</tspan></text>`,
		`<text x="${W / 2}" y="${CARD_H - 50}" text-anchor="middle" font-family="${FONT}" font-size="32" font-weight="700" fill="#fff" opacity=".85">${SITE}</text>`,
		`</svg>`,
	].join("");
}

export async function sharePolaroid(
	el: HTMLElement,
	p: { hy: string; name: string; date: string | null; line: string; text: string },
): Promise<ShareOutcome> {
	const svg = el.querySelector('[data-testid="polaroid-photo"]');
	if (!(svg instanceof SVGSVGElement)) return "failed";
	return shareImage(
		polaroidCardSvg(stillSvg(svg), p),
		"aybuben-photo.png",
		`${p.text} https://${SITE}`,
	);
}
